// src/GDB/components/TemplateGallery.jsx
/**
 * Template Gallery Component
 * 
 * A beautiful, comprehensive UI for browsing and selecting dashboard templates.
 * Features:
 * - Category tabs with icons
 * - Template cards with preview info
 * - Search/filter functionality
 * - Responsive grid layout
 */

import { useState, useEffect, useMemo } from "react";
import { apiGet } from "../api/client";

export default function TemplateGallery({ onSelectTemplate, onClose }) {
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [hoveredTemplate, setHoveredTemplate] = useState(null);

  // Load gallery data
  useEffect(() => {
    setLoading(true);
    setError("");
    
    apiGet("/dashboard-templates/from-gallery")
      .then((data) => {
        setGallery(data);
        if (data?.categories?.length) {
          setSelectedCategory(data.categories[0].id);
        }
      })
      .catch((err) => {
        console.error("Failed to load templates:", err);
        setError("Failed to load template gallery");
      })
      .finally(() => setLoading(false));
  }, []);

  // Get current category data
  const currentCategory = useMemo(() => {
    if (!gallery?.categories || !selectedCategory) return null;
    return gallery.categories.find((c) => c.id === selectedCategory);
  }, [gallery, selectedCategory]);

  // Filter templates by search
  const filteredTemplates = useMemo(() => {
    if (!currentCategory?.templates) return [];
    if (!searchQuery.trim()) return currentCategory.templates;
    
    const query = searchQuery.toLowerCase();
    return currentCategory.templates.filter(
      (t) =>
        t.label?.toLowerCase().includes(query) ||
        t.description?.toLowerCase().includes(query) ||
        t.dashboard_name?.toLowerCase().includes(query)
    );
  }, [currentCategory, searchQuery]);

  // Handle template selection
  const handleSelectTemplate = (template) => {
    onSelectTemplate?.(template, currentCategory);
    onClose?.();
  };

  // Get widget type icon
  const getWidgetIcon = (type) => {
    const icons = {
      bar: "📊", column: "📈", line: "📉", area: "🟦",
      pie: "🥧", doughnut: "🍩", kpi: "🔢", table: "📋",
      gauge: "🎚️", funnel: "🔄", treemap: "🌳", waterfall: "📊",
      radar: "📡", scatter: "🔵", bubble: "🫧",
      stacked_bar: "🧱", stacked_area: "🗺️", combo: "🔀",
    };
    return icons[type] || "📊";
  };

  return (
    <div className="template-gallery-backdrop" onClick={onClose}>
      <div className="template-gallery" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="gallery-header">
          <div className="header-content">
            <div className="header-title-section">
              <h2 className="gallery-title">📚 Dashboard Templates</h2>
              <p className="gallery-subtitle">
                Choose a pre-built template to get started quickly
              </p>
            </div>
            <button className="close-btn" onClick={onClose}>
              ✕
            </button>
          </div>
          
          {/* Search Bar */}
          <div className="search-bar">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            {searchQuery && (
              <button
                className="search-clear"
                onClick={() => setSearchQuery("")}
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="gallery-content">
          {loading ? (
            <div className="gallery-loading">
              <div className="loading-spinner"></div>
              <p>Loading templates...</p>
            </div>
          ) : error ? (
            <div className="gallery-error">
              <span className="error-icon">⚠️</span>
              <p>{error}</p>
              <button className="retry-btn" onClick={() => window.location.reload()}>
                Retry
              </button>
            </div>
          ) : (
            <>
              {/* Category Tabs */}
              <div className="category-sidebar">
                <div className="category-list">
                  {gallery?.categories?.map((cat) => (
                    <button
                      key={cat.id}
                      className={`category-tab ${selectedCategory === cat.id ? "active" : ""}`}
                      onClick={() => setSelectedCategory(cat.id)}
                    >
                      <span className="category-icon">{cat.icon || "📊"}</span>
                      <div className="category-info">
                        <span className="category-label">{cat.label}</span>
                        <span className="category-count">
                          {cat.templates?.length || 0} templates
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Template Grid */}
              <div className="template-grid-container">
                {currentCategory && (
                  <div className="category-header">
                    <span className="category-header-icon">{currentCategory.icon}</span>
                    <div>
                      <h3 className="category-header-title">{currentCategory.label}</h3>
                      <p className="category-header-desc">{currentCategory.description}</p>
                    </div>
                  </div>
                )}

                {filteredTemplates.length === 0 ? (
                  <div className="no-templates">
                    <span className="no-templates-icon">🔍</span>
                    <p>No templates found matching "{searchQuery}"</p>
                  </div>
                ) : (
                  <div className="template-grid">
                    {filteredTemplates.map((template) => (
                      <div
                        key={template.id}
                        className={`template-card ${hoveredTemplate === template.id ? "hovered" : ""}`}
                        onMouseEnter={() => setHoveredTemplate(template.id)}
                        onMouseLeave={() => setHoveredTemplate(null)}
                        onClick={() => handleSelectTemplate(template)}
                      >
                        <div className="template-card-header">
                          <span className="template-icon">{template.icon || "📋"}</span>
                          <span className="widget-count">
                            {template.widgets?.length || 0} widgets
                          </span>
                        </div>
                        
                        <h4 className="template-name">{template.label}</h4>
                        <p className="template-description">{template.description}</p>
                        
                        {/* Widget Preview */}
                        <div className="widget-preview">
                          {template.widgets?.slice(0, 6).map((w, idx) => (
                            <span
                              key={idx}
                              className="widget-badge"
                              title={`${w.title} (${w.widget_type})`}
                            >
                              {getWidgetIcon(w.widget_type)}
                            </span>
                          ))}
                          {template.widgets?.length > 6 && (
                            <span className="widget-more">
                              +{template.widgets.length - 6}
                            </span>
                          )}
                        </div>

                        <div className="template-card-footer">
                          <span className="dashboard-name">{template.dashboard_name}</span>
                          <button className="use-template-btn">
                            Use Template →
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <style>{`
          .template-gallery-backdrop {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.6);
            backdrop-filter: blur(4px);
            z-index: 1000;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 20px;
          }

          .template-gallery {
            background: white;
            border-radius: 16px;
            width: 100%;
            max-width: 1200px;
            max-height: 90vh;
            display: flex;
            flex-direction: column;
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
            overflow: hidden;
          }

          .gallery-header {
            padding: 20px 24px;
            border-bottom: 1px solid #e5e7eb;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
          }

          .header-content {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 16px;
          }

          .gallery-title {
            font-size: 1.5rem;
            font-weight: 700;
            margin: 0;
          }

          .gallery-subtitle {
            margin: 4px 0 0 0;
            opacity: 0.9;
            font-size: 0.9rem;
          }

          .close-btn {
            background: rgba(255, 255, 255, 0.2);
            border: none;
            color: white;
            width: 36px;
            height: 36px;
            border-radius: 50%;
            font-size: 1.2rem;
            cursor: pointer;
            transition: all 0.2s;
          }

          .close-btn:hover {
            background: rgba(255, 255, 255, 0.3);
            transform: scale(1.1);
          }

          .search-bar {
            position: relative;
            display: flex;
            align-items: center;
          }

          .search-icon {
            position: absolute;
            left: 14px;
            font-size: 1rem;
            opacity: 0.7;
          }

          .search-input {
            width: 100%;
            padding: 12px 40px;
            border: none;
            border-radius: 8px;
            font-size: 0.95rem;
            background: rgba(255, 255, 255, 0.95);
            color: #1f2937;
          }

          .search-input::placeholder {
            color: #9ca3af;
          }

          .search-input:focus {
            outline: none;
            box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.3);
          }

          .search-clear {
            position: absolute;
            right: 12px;
            background: #e5e7eb;
            border: none;
            width: 24px;
            height: 24px;
            border-radius: 50%;
            cursor: pointer;
            font-size: 0.8rem;
            color: #6b7280;
          }

          .gallery-content {
            display: flex;
            flex: 1;
            overflow: hidden;
          }

          .category-sidebar {
            width: 240px;
            border-right: 1px solid #e5e7eb;
            background: #f9fafb;
            overflow-y: auto;
            flex-shrink: 0;
          }

          .category-list {
            padding: 12px;
          }

          .category-tab {
            width: 100%;
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 12px 14px;
            border: none;
            background: transparent;
            border-radius: 10px;
            cursor: pointer;
            text-align: left;
            transition: all 0.2s;
            margin-bottom: 4px;
          }

          .category-tab:hover {
            background: #e5e7eb;
          }

          .category-tab.active {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
          }

          .category-icon {
            font-size: 1.5rem;
            width: 36px;
            height: 36px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(255, 255, 255, 0.2);
            border-radius: 8px;
          }

          .category-tab:not(.active) .category-icon {
            background: white;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          }

          .category-info {
            display: flex;
            flex-direction: column;
            gap: 2px;
          }

          .category-label {
            font-weight: 600;
            font-size: 0.9rem;
          }

          .category-count {
            font-size: 0.75rem;
            opacity: 0.7;
          }

          .template-grid-container {
            flex: 1;
            overflow-y: auto;
            padding: 20px;
          }

          .category-header {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-bottom: 20px;
            padding-bottom: 16px;
            border-bottom: 1px solid #e5e7eb;
          }

          .category-header-icon {
            font-size: 2rem;
            width: 50px;
            height: 50px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-radius: 12px;
          }

          .category-header-title {
            margin: 0;
            font-size: 1.25rem;
            font-weight: 600;
            color: #1f2937;
          }

          .category-header-desc {
            margin: 4px 0 0 0;
            color: #6b7280;
            font-size: 0.9rem;
          }

          .template-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
            gap: 16px;
          }

          .template-card {
            background: white;
            border: 1px solid #e5e7eb;
            border-radius: 12px;
            padding: 16px;
            cursor: pointer;
            transition: all 0.3s ease;
            position: relative;
            overflow: hidden;
          }

          .template-card::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 3px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            transform: scaleX(0);
            transition: transform 0.3s ease;
          }

          .template-card:hover {
            border-color: #667eea;
            box-shadow: 0 10px 25px -5px rgba(102, 126, 234, 0.2);
            transform: translateY(-4px);
          }

          .template-card:hover::before {
            transform: scaleX(1);
          }

          .template-card-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 12px;
          }

          .template-icon {
            font-size: 1.75rem;
            width: 44px;
            height: 44px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #f3f4f6;
            border-radius: 10px;
          }

          .widget-count {
            font-size: 0.75rem;
            background: #e0e7ff;
            color: #4338ca;
            padding: 4px 10px;
            border-radius: 20px;
            font-weight: 500;
          }

          .template-name {
            margin: 0 0 8px 0;
            font-size: 1rem;
            font-weight: 600;
            color: #1f2937;
          }

          .template-description {
            margin: 0 0 12px 0;
            font-size: 0.85rem;
            color: #6b7280;
            line-height: 1.4;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }

          .widget-preview {
            display: flex;
            gap: 6px;
            flex-wrap: wrap;
            margin-bottom: 12px;
          }

          .widget-badge {
            font-size: 1.1rem;
            width: 32px;
            height: 32px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #f9fafb;
            border: 1px solid #e5e7eb;
            border-radius: 6px;
            cursor: help;
          }

          .widget-more {
            font-size: 0.75rem;
            color: #6b7280;
            display: flex;
            align-items: center;
            padding: 0 8px;
            background: #f3f4f6;
            border-radius: 6px;
            font-weight: 500;
          }

          .template-card-footer {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding-top: 12px;
            border-top: 1px solid #f3f4f6;
          }

          .dashboard-name {
            font-size: 0.75rem;
            color: #9ca3af;
            font-weight: 500;
            max-width: 140px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .use-template-btn {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border: none;
            padding: 6px 14px;
            border-radius: 6px;
            font-size: 0.8rem;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s;
            opacity: 0;
            transform: translateX(10px);
          }

          .template-card:hover .use-template-btn {
            opacity: 1;
            transform: translateX(0);
          }

          .use-template-btn:hover {
            box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
          }

          .gallery-loading, .gallery-error, .no-templates {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 60px 20px;
            width: 100%;
            color: #6b7280;
          }

          .loading-spinner {
            width: 48px;
            height: 48px;
            border: 4px solid #e5e7eb;
            border-top-color: #667eea;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin-bottom: 16px;
          }

          @keyframes spin {
            to { transform: rotate(360deg); }
          }

          .error-icon, .no-templates-icon {
            font-size: 3rem;
            margin-bottom: 12px;
          }

          .retry-btn {
            margin-top: 12px;
            padding: 8px 20px;
            background: #667eea;
            color: white;
            border: none;
            border-radius: 6px;
            cursor: pointer;
          }

          /* Responsive */
          @media (max-width: 768px) {
            .template-gallery {
              max-height: 100vh;
              border-radius: 0;
            }

            .gallery-content {
              flex-direction: column;
            }

            .category-sidebar {
              width: 100%;
              border-right: none;
              border-bottom: 1px solid #e5e7eb;
              max-height: 200px;
            }

            .category-list {
              display: flex;
              gap: 8px;
              overflow-x: auto;
              padding: 12px;
            }

            .category-tab {
              flex-shrink: 0;
              width: auto;
              padding: 8px 12px;
            }

            .category-info {
              display: none;
            }

            .template-grid {
              grid-template-columns: 1fr;
            }
          }
        `}</style>
      </div>
    </div>
  );
}